import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'

export type SortDirection = 'asc' | 'desc'

export type SortState = {
  field: string
  direction: SortDirection
}

type Props = {
  field: string
  label: string
  sort: SortState | null
  onSortChange: (next: SortState) => void
  align?: 'left' | 'right'
  className?: string
}

export function SortableHeader({ field, label, sort, onSortChange, align = 'left', className = '' }: Props) {
  const active = sort?.field === field
  const direction = active ? sort?.direction : null

  const handleClick = () => {
    if (active && sort?.direction === 'desc') {
      onSortChange({ field, direction: 'asc' })
    } else if (active && sort?.direction === 'asc') {
      onSortChange({ field, direction: 'desc' })
    } else {
      onSortChange({ field, direction: 'desc' })
    }
  }

  const alignClass = align === 'right' ? 'text-right justify-end' : 'text-left justify-start'

  return (
    <th
      scope="col"
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${alignClass} ${className}`}
    >
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1 ${alignClass} text-content-muted hover:text-content transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring rounded`}
        aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
      >
        <span>{label}</span>
        {!active && <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" aria-hidden />}
        {active && direction === 'desc' && <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
        {active && direction === 'asc' && <ChevronUp className="h-3.5 w-3.5" aria-hidden />}
      </button>
    </th>
  )
}

export function sortStateToParam(sort: SortState | null): string | undefined {
  if (!sort) return undefined
  return sort.direction === 'desc' ? `-${sort.field}` : sort.field
}

export function loadSortFromStorage(key: string, fallback: SortState): SortState {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed.field === 'string' && (parsed.direction === 'asc' || parsed.direction === 'desc')) {
      return parsed as SortState
    }
  } catch {
    // ignore
  }
  return fallback
}

export function saveSortToStorage(key: string, sort: SortState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, JSON.stringify(sort))
  } catch {
    // ignore
  }
}
